import React, {useEffect, useMemo, useState} from 'react';
import {TextInput, View} from 'react-native';
import {
  topone,
  toptwo,
  content,
  bottom,
  CardOuterBg,
  CardTopRightImg,
  invitationColors,
  invitationApplyColor,
} from './invitation.variables';
import ImageBackgroundBasic from './ImageBackgroundBasic';
import Text from '@basicComponents/text';
import LinearGradient from '@basicComponents/linear-gradient';
import {BasicObject, SafeAny} from '@/types';
import {
  basicColor,
  borderRadius,
  flex,
  fontSize,
  margin,
  padding,
  position,
} from '@/components/style';
import LazyImage from '@/components/basic/image';
import {PhoneIcon, SaveIcon, SignGiftBg} from './svg.variables';
import {NativeTouchableOpacity} from '@/components/basic/touchable-opacity';
import {sendCode, userLogin} from '../login/login.service';
import {
  getInitialInvitationInviteCode,
  resolveInviteCode,
  useInvalidInviteCodeConfirm,
} from './invite-code.utils';
import globalStore from '@/services/global.state';
import {clearRechargeGiftAccountStorage} from '@/pages/home/components/recharge-gift/recharge-gift-storage';
import i18n from '@/i18n';
import baseVariable from '@/style/base.variable';
import {downloadApk, inApp, useResponsiveDimensions} from '@/utils';
import theme from '@/style';
import './polyfle.css';

const outline: SafeAny = {
  outline: 0,
};

const InvitationApply = (props: SafeAny) => {
  const [userPhone, setUserPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpTime, setOtpTime] = useState(59);
  const [hasOtp, setHasOtp] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const {route} = props;
  const routeCode = String((route?.params as BasicObject)?.code || '').trim();
  const [inviteCode, setInviteCode] = useState(() =>
    getInitialInvitationInviteCode(routeCode),
  );
  const {width: screenWidth, height: screenHeight} = useResponsiveDimensions();
  const {renderInvalidCodeModal, showInvalidInviteCode, hideInvalidInviteCode} =
    useInvalidInviteCodeConfirm();

  const formSize = useMemo(() => {
    const scale = screenWidth / 350;

    return {
      formBox: {
        width: screenWidth - theme.paddingSize.l * 2,
        height: ((screenWidth - theme.paddingSize.l * 2) / 351) * 372,
      },
      formTitleBg: {
        height:
          ((screenWidth - (theme.paddingSize.l + theme.paddingSize.s) * 2) /
            335) *
          67,
        paddingLeft: scale * 25,
      },
      titleSize: {
        width: scale * 200,
      },
      button: {
        height: scale * 48,
      },
    };
  }, [screenWidth]);

  useEffect(() => {
    if (!hasOtp) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setOtpTime(current => {
        if (current <= 1) {
          window.clearInterval(timer);
          setHasOtp(false);
          return 59;
        }
        return current - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [hasOtp]);

  const requestOtp = () => {
    if (!userPhone || otpLoading) {
      return;
    }
    setOtpLoading(true);
    sendCode(userPhone)
      .then(() => {
        setHasOtp(true);
        setOtpTime(59);
        globalStore.globalTotal.next({
          type: 'success',
          message: i18n.t('tip.success'),
        });
      })
      .finally(() => setOtpLoading(false));
  };

  const register = async () => {
    if (otpLoading || !userPhone || !otpCode) {
      return;
    }
    globalStore.globalLoading.next(true);
    try {
      let invalidInviteCode = false;
      const resolvedCode = await resolveInviteCode(
        inviteCode,
        showInvalidInviteCode,
        () => {
          invalidInviteCode = true;
          setInviteCode('');
        },
      );
      if (resolvedCode) {
        localStorage.setItem('invitationCode', resolvedCode);
      } else {
        localStorage.removeItem('invitationCode');
      }
      const res = await userLogin(userPhone, otpCode, resolvedCode || '');
      if (invalidInviteCode) {
        hideInvalidInviteCode();
      }
      globalStore.globalTotal.next({
        type: 'success',
        message: i18n.t('tip.success'),
      });
      clearRechargeGiftAccountStorage();
      globalStore.token = res.token;
      if (inApp) {
        window.location.href = 'index/home';
      } else {
        downloadApk();
      }
    } finally {
      globalStore.globalLoading.next(false);
    }
  };

  return (
    <View style={[theme.position.rel, {height: screenHeight}]}>
      <ImageBackgroundBasic Img={topone} />
      <ImageBackgroundBasic Img={toptwo} />
      <ImageBackgroundBasic Img={content} />
      <ImageBackgroundBasic Img={bottom} />
      <View
        style={[
          position.abs,
          {
            paddingBottom: theme.paddingSize.l * 2,
            bottom: theme.paddingSize.l * 2,
          },
          theme.fill.fillW,
          padding.lrl,
        ]}>
        <LazyImage
          imageUrl={CardOuterBg}
          occupancy="#0000"
          width="100%"
          height={formSize.formBox.height}
        />
        <View style={[position.abs, formSize.formBox]}>
          <View style={[position.abs, {right: -8, top: 0}]}>
            <LazyImage
              imageUrl={CardTopRightImg}
              occupancy="#0000"
              width={110}
              height={42}
            />
          </View>
          <View style={[theme.fill.fill, theme.padding.tbs]}>
            <SignGiftBg
              width="100%"
              height={formSize.formTitleBg.height}
              fill={invitationColors.signGiftBgColor}
              style={theme.position.abs}
            />
            <View style={[formSize.formTitleBg, theme.flex.centerByRow]}>
              <Text
                fontSize={20}
                calc
                color="#FFF"
                style={[formSize.titleSize]}
                fontFamily="fontInterBold">
                {i18n.t('referral.label.title')}
              </Text>
            </View>
            <View
              style={[
                padding.lrl,
                padding.btml,
                margin.lrs,
                theme.background.white,
                theme.flex.flex1,
                {
                  borderEndEndRadius: theme.borderRadiusSize.xl,
                  borderEndStartRadius: theme.borderRadiusSize.xl,
                },
              ]}>
              <View style={[theme.flex.flex1, theme.flex.center]}>
                <View
                  style={[
                    {
                      height: formSize.button.height,
                      borderWidth: 1,
                      borderColor: invitationApplyColor.borderColor,
                      backgroundColor: invitationApplyColor.backgroundColor,
                    },
                    padding.lrl,
                    theme.fill.fillW,
                    borderRadius.m,
                    flex.row,
                    flex.centerByCol,
                  ]}>
                  <PhoneIcon width={24} height={24} />
                  <TextInput
                    placeholder="Phone number"
                    inputMode="numeric"
                    style={[
                      {
                        height: formSize.button.height,
                        marginLeft: 16,
                        borderWidth: 0,
                        borderColor: 'none',
                      },
                      flex.flex1,
                      outline,
                    ]}
                    value={userPhone}
                    maxLength={10}
                    placeholderTextColor={baseVariable.fontColor.secAccent}
                    onChangeText={value =>
                      setUserPhone(value.replace(/\D/g, ''))
                    }
                  />
                </View>
                <View
                  style={[
                    {
                      height: formSize.button.height,
                      borderWidth: 1,
                      borderColor: invitationApplyColor.borderColor,
                      backgroundColor: invitationApplyColor.backgroundColor,
                    },
                    padding.lrl,
                    theme.fill.fillW,
                    borderRadius.m,
                    flex.row,
                    flex.centerByCol,
                    margin.topl,
                  ]}>
                  <SaveIcon width={24} height={24} />
                  <TextInput
                    placeholder="OTP"
                    inputMode="numeric"
                    underlineColorAndroid="transparent"
                    style={[
                      {
                        height: formSize.button.height / 2,
                        marginLeft: 16,
                        borderWidth: 0,
                        borderColor: 'none',
                        fontSize: 15,
                      },
                      flex.flex1,
                      outline,
                    ]}
                    value={otpCode}
                    maxLength={6}
                    placeholderTextColor={baseVariable.fontColor.secAccent}
                    onChangeText={value => {
                      if (!value || /^\d+$/.test(value)) {
                        setOtpCode(value);
                      }
                    }}
                  />
                  {hasOtp ? (
                    <Text
                      fontSize={fontSize.s}
                      accent
                      style={{lineHeight: fontSize.s}}
                      textAlign="center"
                      blod>
                      {otpTime}s
                    </Text>
                  ) : (
                    <NativeTouchableOpacity onPress={requestOtp}>
                      <Text
                        color={basicColor.primary}
                        fontSize={15}
                        fontWeight="bold">
                        Send
                      </Text>
                    </NativeTouchableOpacity>
                  )}
                </View>
              </View>
              <NativeTouchableOpacity onPress={register}>
                <View
                  style={[
                    flex.flex1,
                    {borderRadius: formSize.button.height / 2},
                  ]}>
                  <LinearGradient
                    style={[formSize.button, theme.fill.fillW, flex.center]}
                    start={{x: 0, y: 0}}
                    end={{x: 1, y: 1}}
                    colors={basicColor.primaryLinearGradient}>
                    <View>
                      <Text
                        fontSize={16}
                        color="#fff"
                        fontFamily="fontInterBold">
                        {i18n.t('label.logIn')}
                      </Text>
                    </View>
                  </LinearGradient>
                </View>
              </NativeTouchableOpacity>
              <Text
                accent
                size="small"
                calc
                textAlign="center"
                style={[margin.tops]}>
                {i18n.t('referral.tip.desc')}
              </Text>
            </View>
          </View>
        </View>
      </View>
      {renderInvalidCodeModal}
    </View>
  );
};

export default InvitationApply;
